import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert
} from '@mui/material'
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Visibility as VisibilityIcon,
  Message as MessageIcon,
  Email as EmailIcon,
  Sms as SmsIcon
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import integrationService from '../services/integrationService'

interface Template {
  id: string
  name: string
  channel: string  // WHATSAPP, EMAIL, SMS
  language: string
  content?: string  // For EMAIL/SMS
  whatsapp_message?: string  // For WHATSAPP
  sms_message?: string  // For SMS
  variables: string[]
  usage_count?: number
  is_active: boolean
  approval_status?: string  // DRAFT, PENDING_APPROVAL, APPROVED, REJECTED, ARCHIVED
  created_at: string
  updated_at: string
}

// Helper function to get template content based on channel
const getTemplateContent = (template: Template): string => {
  if (template.channel === 'WHATSAPP' && template.whatsapp_message) {
    return template.whatsapp_message
  }
  if (template.channel === 'SMS' && template.sms_message) {
    return template.sms_message
  }
  return template.content || ''
}

const IntegrationTemplatesPage: React.FC = () => {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [filterType, setFilterType] = useState<string>('all')
  const [filterApprovalStatus, setFilterApprovalStatus] = useState<string>('all')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null)

  // Load templates
  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await integrationService.getTemplates()
      setTemplates(data as unknown as Template[])
    } catch (err: any) {
      setError(err.message || 'Failed to load templates')
    } finally {
      setLoading(false)
    }
  }

  // Filter templates
  const filteredTemplates = templates.filter(template => {
    const templateContent = getTemplateContent(template)
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      templateContent.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesType = filterType === 'all' || template.channel === filterType
    const matchesApproval = filterApprovalStatus === 'all' || template.approval_status === filterApprovalStatus
    return matchesSearch && matchesType && matchesApproval
  })

  // Get unique template types (channels)
  const templateTypes = ['all', ...Array.from(new Set(templates.map(t => t.channel).filter(Boolean)))]

  // Get approval status color
  const getApprovalStatusColor = (status?: string): 'default' | 'warning' | 'success' | 'error' | 'info' => {
    switch (status) {
      case 'APPROVED':
        return 'success'
      case 'DRAFT':
        return 'default'
      case 'PENDING_APPROVAL':
        return 'warning'
      case 'REJECTED':
        return 'error'
      case 'ARCHIVED':
        return 'info'
      default:
        return 'default'
    }
  }

  // Get template type icon
  const getTemplateIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'whatsapp':
        return <MessageIcon />
      case 'email':
        return <EmailIcon />
      case 'sms':
        return <SmsIcon />
      default:
        return <MessageIcon />
    }
  }

  // Handle create template
  const handleCreate = () => {
    navigate('/admin/integrations/templates/create')
  }

  // Handle edit template
  const handleEdit = (templateId: string) => {
    navigate(`/admin/integrations/templates/${templateId}/edit`)
  }

  // Handle duplicate template
  const handleDuplicate = async (template: Template) => {
    try {
      const duplicatedTemplate = {
        template_key: `${template.id}_copy`,
        template_name: `${template.name} (Copy)`,
        description: '',
        whatsapp_message: template.whatsapp_message,
        sms_message: template.sms_message,
        language_code: template.language,
        is_active: template.is_active
      }
      await integrationService.createTemplate(duplicatedTemplate)
      loadTemplates()
    } catch (err: any) {
      setError(err.message || 'Failed to duplicate template')
    }
  }

  // Handle delete template
  const handleDeleteClick = (template: Template) => {
    setTemplateToDelete(template)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return

    try {
      await integrationService.deleteTemplate(templateToDelete.id)
      setDeleteDialogOpen(false)
      setTemplateToDelete(null)
      loadTemplates()
    } catch (err: any) {
      setError(err.message || 'Failed to delete template')
    }
  }

  // Handle preview template
  const handlePreview = (templateId: string) => {
    navigate(`/admin/integrations/templates/${templateId}/preview`)
  }

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading templates...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Notification Templates
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreate}
        >
          Create Template
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField
          placeholder="Search templates..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
          sx={{ flexGrow: 1 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Template Type</InputLabel>
          <Select
            value={filterType}
            label="Template Type"
            onChange={(e) => setFilterType(e.target.value)}
          >
            {templateTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type === 'all' ? 'All Types' : type.toUpperCase()}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Approval Status</InputLabel>
          <Select
            value={filterApprovalStatus}
            label="Approval Status"
            onChange={(e) => setFilterApprovalStatus(e.target.value)}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="APPROVED">Approved</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
            <MenuItem value="REJECTED">Rejected</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Templates Grid */}
      {filteredTemplates.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No templates found
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {searchQuery || filterType !== 'all'
              ? 'Try adjusting your filters'
              : 'Create your first notification template to get started'}
          </Typography>
          {!searchQuery && filterType === 'all' && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
              Create Template
            </Button>
          )}
        </Box>
      ) : (
        <Grid container spacing={3}>
          {filteredTemplates.map((template) => (
            <Grid item xs={12} md={6} lg={4} key={template.id}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    {getTemplateIcon(template.channel)}
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      {template.name}
                    </Typography>
                    {!template.is_active && (
                      <Chip label="Inactive" size="small" color="default" />
                    )}
                    {template.approval_status && template.approval_status !== 'APPROVED' && (
                      <Chip
                        label={template.approval_status.replace('_', ' ')}
                        size="small"
                        color={getApprovalStatusColor(template.approval_status)}
                      />
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Chip
                      label={template.channel.toUpperCase()}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Chip
                      label={template.language.toUpperCase()}
                      size="small"
                      variant="outlined"
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {getTemplateContent(template) || '(No content)'}
                  </Typography>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="textSecondary">
                      Used {template.usage_count || 0} times
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {template.variables.length} variables
                    </Typography>
                  </Box>
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handlePreview(template.id)}
                      title="Preview"
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(template.id)}
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDuplicate(template)}
                      title="Duplicate"
                    >
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteClick(template)}
                    title="Delete"
                    disabled={(template.usage_count || 0) > 0}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the template "{templateToDelete?.name}"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default IntegrationTemplatesPage
